import { Text, Box, Flex, Button, Card } from "@radix-ui/themes";
import { ExternalLink, FileText } from "lucide-react";

interface QuickStartStep {
  text: string;
}

interface WelcomeViewProps {
  /** Main headline text */
  title: string;
  /** Descriptive text below the headline */
  description: string;
  /** Steps to show in the quick start guide */
  quickStartSteps: QuickStartStep[];
  /** Primary action button configuration */
  primaryAction: {
    text: string;
    icon: React.ReactNode;
    onClick: () => void;
  };
  /** Optional secondary action (documentation link) */
  secondaryAction?: {
    text?: string;
    url?: string;
  };
  /** Optional padding configuration */
  padding?: {
    top?: string;
    left?: string;
  };
}

export const WelcomeView = ({
  title,
  description,
  quickStartSteps,
  primaryAction,
  secondaryAction = {
    text: "View Anythink Docs",
    url: "https://docs.anythink.cloud"
  },
  padding
}: WelcomeViewProps) => (
  <Flex
    direction="column"
    gap="6"
    style={{
      maxWidth: "480px",
      paddingTop: padding?.top || "0",
      paddingLeft: padding?.left || "0",
    }}
  >
    <Box>
      <Text size="4" weight="bold" style={{ display: "block", marginBottom: "var(--space-2)" }}>
        {title}
      </Text>
      <Text size="3" color="gray" style={{ lineHeight: 1.6 }}>
        {description}
      </Text>
    </Box>

    <Card style={{ width: "100%", padding: "var(--space-4)" }}>
      <Flex direction="column" gap="4">
        <Text size="4" weight="medium">
          🚀 Quick Start Guide
        </Text>
        <Flex direction="column" gap="2" style={{ fontSize: "var(--font-size-2)" }}>
          {quickStartSteps.map((step, index) => (
            <Text key={index} color="gray">
              {index + 1}. {step.text}
            </Text>
          ))}
        </Flex>
      </Flex>
    </Card>

    <Flex direction="column" gap="3" style={{ width: "100%" }}>
      <Button size="3" onClick={primaryAction.onClick} style={{ width: "100%" }}>
        {primaryAction.icon}
        {primaryAction.text}
      </Button>
      
      {secondaryAction && (
        <Button
          variant="outline"
          size="3"
          onClick={() => window.open(secondaryAction.url, "_blank")}
          style={{ width: "100%" }}
        >
          <FileText size={16} />
          {secondaryAction.text}
          <ExternalLink style={{ width: "14px", height: "14px" }} />
        </Button>
      )}
    </Flex>
  </Flex>
);

export default WelcomeView;